/**
 * Las reglas de la pantalla de Workflows, puras (sin Angular). Son espejo de
 * `modules/workflow/dominio/definicion.js` del núcleo, que es el que manda: la pantalla las usa para
 * avisar antes de guardar, y el servidor las vuelve a revisar siempre.
 */
import { PerfilWorkflow } from 'src/app/services/workflows/workflows.service';

export interface PerfilEditable { rol: string; devuelve_a: number | null; puede_rechazar: boolean; }

/** Después de subir, bajar o quitar un perfil, una devolución que ya no apunta atrás se apaga. */
export function limpiarDevoluciones(lista: PerfilEditable[]): PerfilEditable[] {
  return lista.map((p, i) => ({
    ...p,
    devuelve_a: i === 0 || p.devuelve_a === null || p.devuelve_a > i ? null : p.devuelve_a,
  }));
}

/** Mover un perfil `delta` lugares, arrastrando su devolución por ROL y no por número. */
export function mover(lista: PerfilEditable[], i: number, delta: number): PerfilEditable[] {
  const j = i + delta;
  if (j < 0 || j >= lista.length) return lista;
  const rolDestino = lista.map((p) => (p.devuelve_a === null ? null : lista[p.devuelve_a - 1]?.rol ?? null));
  const orden = lista.map((_, k) => k);
  [orden[i], orden[j]] = [orden[j], orden[i]];
  const nueva = orden.map((k) => ({ ...lista[k], _dest: rolDestino[k] } as any));
  const posDe = new Map(nueva.map((p: any, k) => [p.rol, k + 1]));
  return limpiarDevoluciones(nueva.map(({ _dest, ...p }: any) => ({ ...p, devuelve_a: _dest ? (posDe.get(_dest) ?? null) : null })));
}

export function quitar(lista: PerfilEditable[], i: number): PerfilEditable[] {
  const rolDestino = lista.map((p) => (p.devuelve_a === null ? null : lista[p.devuelve_a - 1]?.rol ?? null));
  const quedan = lista.map((p, k) => ({ p, dest: rolDestino[k] })).filter((_, k) => k !== i);
  const posDe = new Map(quedan.map((x, k) => [x.p.rol, k + 1]));
  return limpiarDevoluciones(quedan.map(({ p, dest }) => ({ ...p, devuelve_a: dest ? (posDe.get(dest) ?? null) : null })));
}

/** Los errores que el servidor también va a dar, para mostrarlos antes de guardar. */
export function errores(lista: PerfilEditable[], paraPublicar = false): string[] {
  const e: string[] = [];
  if (paraPublicar && lista.length === 0) e.push('Un workflow necesita al menos un perfil para publicarse.');
  const vistos = new Set<string>();
  lista.forEach((p, i) => {
    if (!p.rol) e.push(`El perfil ${i + 1} no tiene rol.`);
    else if (vistos.has(p.rol)) e.push(`El perfil ${i + 1} repite un rol: un perfil aparece una sola vez.`);
    vistos.add(p.rol);
  });
  return e;
}

export const aEditable = (perfiles: PerfilWorkflow[]): PerfilEditable[] =>
  perfiles.map((p) => ({ rol: p.rol, devuelve_a: p.devuelve_a, puede_rechazar: !!p.puede_rechazar }));

export const iguales = (a: PerfilEditable[], b: PerfilEditable[]): boolean => JSON.stringify(a) === JSON.stringify(b);

export function fecha(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' }).replace('.', '');
}
